import React, { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useForm, Controller } from "react-hook-form";
import { X } from "lucide-react";
import ResponseModal from "../response-modal";
import userService from "../../../services/userService";
import customerService from "../../../services/customerService";
import carrierService from "../../../services/carrierService";
import { filterEmptyArrayItems } from "../../../utils/validation";
import "./styles.scss";
import { CustomIcon } from "../../UI";
import AvatarSection from "./components/AvatarSection";
import UserFormFields from "./components/UserFormFields";
import CustomerFormFields from "./components/CustomerFormFields";
import CarrierFormFields from "./components/CarrierFormFields";

export function AddForm({
  onClose,
  userData,
  formType = "users",
  setIsOpen,
  isOpen = false,
  onSuccess,
  isEditMode = false,
  itemId = null,
}) {
  const dialogRef = useRef(null);
  const dataLoadedRef = useRef(false); // Prevent duplicate data loading
  const selectionDataLoadedRef = useRef(false); // Prevent duplicate selection data loading
  const normalizedFormType = formType === "Customers" ? "customers" : formType;
  const isUserForm = normalizedFormType === "users";
  const isCustomerForm = normalizedFormType === "customers";
  const isCarrierForm = normalizedFormType === "carriers";

  // Default values based on form type
  const defaultValues = useMemo(() => {
    const base = {
      // Common fields
      fullName: "",
      email: "",
      companyName: "",
      phone: "",
      message: "",
      role: "",
      password: "",
      confirmPassword: "",
      active: true,

      // User specific
      allowedCustomers: [],

      // Customer specific
      type: "customer",
      customerAddress: {
        address: "",
        city: "",
        state: "",
        zipCode: "",
      },
      customerPaymentMethod: "",
      customerPaymentTerms: "",
      customerCreditLimit: "",
      representativePeoples: [],
      allowedUsers: [],

      // Carrier specific
      dba: "",
      people: [],
      mcNumber: "",
      dotNumber: "",
      carrierAddress: {
        address: "",
        city: "",
        state: "",
        zipCode: "",
      },
      equipment: [
        { type: "", sizes: [{ value: "", custom: "" }] }
      ],
      capabilities: [],
      certifications: [],
      carrierRouting: "",
      carrierBankAccount: "",
      carrierAccountNumber: "",

      // Files
      avatarFile: null,
      userFiles: [],
      existingUserFiles: [],
      customerFiles: [],
      existingCustomerFiles: [],
      carrierFiles: [],
      existingCarrierFiles: [],
    };
    return base;
  }, []);

  // Initialize react-hook-form
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  // Watch form values
  const formValues = watch();

  // UI state (not form data)
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [loading, setLoading] = useState(false);

  // Selection dropdowns state
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedCustomersData, setSelectedCustomersData] = useState([]);
  const [selectedUsersData, setSelectedUsersData] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showCustomersDropdown, setShowCustomersDropdown] = useState(false);
  const [showUsersDropdown, setShowUsersDropdown] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [userSearchTerm, setUserSearchTerm] = useState("");

  // File errors
  const [userFileError, setUserFileError] = useState("");
  const [customerFileError, setCustomerFileError] = useState("");
  const [carrierFileError, setCarrierFileError] = useState("");
  
  // Response modal state
  const [responseModal, setResponseModal] = useState({
    isOpen: false,
    message: "",
    status: "error",
    title: "",
  });

  const showResponseModal = (message, status = "error", title = "") => {
    setResponseModal({
      isOpen: true,
      message,
      status,
      title,
    });
  };

  const closeResponseModal = () => {
    setResponseModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Equipment Type options
  const equipmentTypeOptions = [
    "Auto Carrier",
    "Car Hauler/Enclosed Trailer",
    "Pickups",
    "Semi Truck",
    "Dry Van",
    "Reefer",
    "Box Truck",
    "Flatbed",
    "Step Deck",
    "Hot Shot",
    "Conestoga",
    "Curtain Van",
    "Sprinter Van",
    "Heavy Haul",
    "RGN",
    "Extendable RGN",
    "Lowboy",
    "Tilt Bed",
    "Landoll",
    "Power Only",
    "Tanker Endorsement",
    "Hopper Bottom",
    "Perimeter",
    "Flat Deck",
    "Dual Lane Transporter",
    "Super Dual Lane Transporter",
    "Drayage/Intermodal",
  ];

  const sizeOptions = [
    "16'",
    "24'",
    "26'",
    "40'",
    "48'",
    "53'",
    "N/A",
    "Other",
  ];

  const createEmptyEquipmentGroup = () => ({
    type: "",
    sizes: [{ value: "", custom: "" }]
  });

  const normalizeEquipmentGroups = (equipment = []) => {
    if (!Array.isArray(equipment) || equipment.length === 0) {
      return [createEmptyEquipmentGroup()];
    }

    return equipment.map(item => {
      const sizes = Array.isArray(item?.sizes) && item.sizes.length > 0
        ? item.sizes.map(size => {
            const trimmed = String(size || "").trim();
            if (!trimmed) return { value: "", custom: "" };
            if (sizeOptions.includes(trimmed)) {
              return { value: trimmed, custom: "" };
            }
            return { value: "Other", custom: trimmed };
          })
        : [{ value: "", custom: "" }];

      return {
        type: String(item?.type || "").trim(),
        sizes
      };
    });
  };

  const buildEquipmentPayload = (equipmentGroups = []) => {
    if (!Array.isArray(equipmentGroups)) return [];
    return equipmentGroups
      .map(group => {
        const type = String(group?.type || "").trim();
        const sizes = Array.isArray(group?.sizes)
          ? group.sizes
              .map(size => {
                const value = size?.value === "Other" ? size?.custom : size?.value;
                return String(value || "").trim();
              })
              .filter(Boolean)
          : [];
        return type ? { type, sizes } : null;
      })
      .filter(Boolean);
  };

  const deriveEquipmentMeta = (equipmentPayload = []) => {
    const equipmentType = equipmentPayload.map(item => item.type);
    const size = Array.from(new Set(equipmentPayload.flatMap(item => item.sizes || [])));
    return { equipmentType, size };
  };

  const getEquipmentFromCarrier = (carrier) => {
    if (Array.isArray(carrier?.equipment) && carrier.equipment.length > 0) {
      return carrier.equipment;
    }

    const equipmentType = Array.isArray(carrier?.equipmentType)
      ? carrier.equipmentType
      : carrier?.equipmentType
        ? [carrier.equipmentType]
        : [];
    const size = Array.isArray(carrier?.size)
      ? carrier.size
      : carrier?.size
        ? [carrier.size]
        : [];

    return equipmentType.map(type => ({ type, sizes: size }));
  };

  const capabilitiesOptions = [
    "Lift Gate",
    "Winch",
    "Team Drivers",
    "Driver Assistance",
    "Residential pickup/delivery",
    "Inside pickup/delivery",
    "Appointment-based deliveries",
    "Cross-docking/transloading",
    "White-glove",
    "Final-mile",
    "Blind Shipments",
  ];

  const certificationsOptions = [
    "HAZMAT",
    "TWIC",
    "SmartWay Certified",
    "C-TPAT",
    "ISO Certified",
    "UIIA Member",
    "Bonded Carrier",
    "Minority-Owned",
    "Women-Owned",
    "Veteran-Owned",
    "Disability-Owned",
  ];

  // Load data when editing
  // Priority: use userData from props if available, otherwise fetch from API
  useEffect(() => {
    if (!isOpen) {
      dataLoadedRef.current = false; // Reset when modal closes
      return;
    }

    // Prevent duplicate loading
    if (dataLoadedRef.current) return;

    // If userData is provided via props, use it directly (no API call needed)
    if (userData && isOpen && isEditMode) {
      dataLoadedRef.current = true; // Mark as loaded
      // Load from userData prop
      if (isUserForm) {
        // Extract allowedCustomers IDs and save full objects for display
        const allowedCustomersIds =
          userData.allowedCustomers?.map((c) =>
            typeof c === "object" ? (c.id || c._id).toString() : c.toString()
          ) || [];

        reset({
          fullName: userData.fullName || "",
          email: userData.email || "",
          companyName: userData.companyName || "",
          phone: userData.phoneNumber || "",
          role: userData.role || "",
          active: typeof userData.active === "boolean" ? userData.active : true,
          message: userData.message || "",
          allowedCustomers: allowedCustomersIds,
          existingUserFiles: userData.pdfs || [],
          userFiles: [],
          avatarUrl: userData.avatarUrl || null,
        });
        
        // Save full customer objects for display (if populated from server)
        if (
          userData.allowedCustomers &&
          userData.allowedCustomers.length > 0 &&
          typeof userData.allowedCustomers[0] === "object" &&
          userData.allowedCustomers[0].companyName
        ) {
          setSelectedCustomersData(userData.allowedCustomers);
        } else {
          // Clear if no valid data
          setSelectedCustomersData([]);
        }
        
        if (userData.avatarUrl) setAvatarUrl(userData.avatarUrl);
      } else if (isCustomerForm) {
        // Extract allowedUsers IDs and save full objects for display
        const allowedUsersIds =
          userData.allowedUsers?.map((u) =>
            typeof u === "object" ? (u.id || u._id).toString() : u.toString()
          ) || [];

        reset({
          companyName: userData.companyName || "",
          type: userData.type || "customer",
          email: userData.email || "",
          message: userData.message || "",
          customerAddress: {
            address: userData.customerAddress || "",
            city: userData.customerCity || "",
            state: userData.customerState || "",
            zipCode: userData.customerZipCode || "",
          },
          customerPaymentMethod: userData.paymentMethod || "",
          customerPaymentTerms: userData.paymentTerms || "",
          customerCreditLimit: userData.creditLimit?.toString() || "",
          representativePeoples: Array.isArray(userData.representativePeoples)
            ? userData.representativePeoples.map(person => ({
                fullName: person.fullName || '',
                email: person.email || '',
                phoneNumber: person.phoneNumber || ''
              }))
            : [],
          allowedUsers: allowedUsersIds,
          existingCustomerFiles: userData.pdfs || [],
          customerFiles: [],
        });
        
        // Save full user objects for display (if populated from server)
        if (
          userData.allowedUsers &&
          userData.allowedUsers.length > 0 &&
          typeof userData.allowedUsers[0] === "object" &&
          (userData.allowedUsers[0].firstName || userData.allowedUsers[0].email)
        ) {
          setSelectedUsersData(userData.allowedUsers);
        } else {
          // Clear if no valid data
          setSelectedUsersData([]);
        }
      } else if (isCarrierForm) {
        const equipmentGroups = normalizeEquipmentGroups(getEquipmentFromCarrier(userData));
        reset({
          fullName: userData.fullName || "",
          companyName: userData.companyName || "",
          dba: userData.dba || "",
          type: userData.type || "driver",
          email: userData.email || "",
          message: userData.message || "",
          mcNumber: userData.mcNumber || "",
          dotNumber: userData.dotNumber || "",
          carrierAddress: {
            address: userData.carrierAddress || "",
            city: userData.carrierCity || "",
            state: userData.carrierState || "",
            zipCode: userData.carrierZipCode || "",
          },
          equipment: equipmentGroups,
          capabilities: userData.capabilities || [],
          certifications: userData.certifications || [],
          carrierRouting: userData.routing || "",
          carrierBankAccount: userData.bankAccount || "",
          carrierAccountNumber: userData.accountNumber || "",
          existingCarrierFiles: userData.pdfs || [],
          carrierFiles: [],
          people: Array.isArray(userData.people) ? userData.people : [],
        });
      }
      return; // Exit early - don't fetch from API if userData is provided
    }

    // Only fetch from API if userData is NOT provided and we're in edit mode
    if (isEditMode && itemId && isOpen && !userData && !dataLoadedRef.current) {
      dataLoadedRef.current = true; // Mark as loading to prevent duplicates
      const fetchItemData = async () => {
        try {
          setLoading(true);
          let response;
          if (isUserForm) {
            response = await userService.getUserById(itemId);
          } else if (isCustomerForm) {
            response = await customerService.getCustomerById(itemId);
          } else if (isCarrierForm) {
            response = await carrierService.getCarrierById(itemId);
          }

          const item = response?.data?.data || response?.data || response;

          // Reset form with fetched data
          if (isUserForm) {
            const allowedCustomersIds = item.allowedCustomers?.map((c) =>
              typeof c === "object"
                ? (c.id || c._id).toString()
                : c.toString()
            ) || [];
            
            reset({
              fullName: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
              email: item.email || "",
              companyName: item.companyName || "",
              phone: item.phoneNumber || "",
              role: item.role || "",
              active: item.status === "active" || item.active === true,
              message: item.message || "",
              allowedCustomers: allowedCustomersIds,
              existingUserFiles: item.pdfs || [],
              userFiles: [],
              avatarUrl: item.profileImage || null,
            });
            
            // Save full customer objects for display (if populated from server)
            if (
              item.allowedCustomers &&
              item.allowedCustomers.length > 0 &&
              typeof item.allowedCustomers[0] === "object" &&
              item.allowedCustomers[0].companyName
            ) {
              setSelectedCustomersData(item.allowedCustomers);
            } else {
              setSelectedCustomersData([]);
            }
            
            if (item.profileImage) setAvatarUrl(item.profileImage);
          } else if (isCustomerForm) {
            // Extract allowedUsers IDs and save full objects for display
            const allowedUsersIds =
              item.allowedUsers?.map((u) =>
                typeof u === "object"
                  ? (u.id || u._id).toString()
                  : u.toString()
              ) || [];

            reset({
              companyName: item.companyName || "",
              type: item.type || "customer",
              email: item.emails?.[0] || item.email || "",
              message: item.phoneNumber || "",
              customerAddress: {
                address: item.customerAddress?.address || "",
                city: item.customerAddress?.city || "",
                state: item.customerAddress?.state || "",
                zipCode: item.customerAddress?.zipCode || "",
              },
              customerPaymentMethod: item.paymentMethod || "",
              customerPaymentTerms: item.paymentTerms || "",
              customerCreditLimit: item.creditLimit?.toString() || "",
              representativePeoples: Array.isArray(item.representativePeoples)
                ? item.representativePeoples.map(person => ({
                    fullName: person.fullName || '',
                    email: person.email || '',
                    phoneNumber: person.phoneNumber || ''
                  }))
                : [],
              allowedUsers: allowedUsersIds,
              existingCustomerFiles: item.pdfs || [],
              customerFiles: [],
            });
            
            // Save full user objects for display (if populated from server)
            if (
              item.allowedUsers &&
              item.allowedUsers.length > 0 &&
              typeof item.allowedUsers[0] === "object" &&
              (item.allowedUsers[0].firstName || item.allowedUsers[0].email)
            ) {
              setSelectedUsersData(item.allowedUsers);
            } else {
              setSelectedUsersData([]);
            }
          } else if (isCarrierForm) {
            // people array instead of type
            const carrierPeople = item.people || item.data?.people || [];
            const equipmentGroups = normalizeEquipmentGroups(getEquipmentFromCarrier(item));
            
            reset({
              fullName: item.name || item.companyName || "",
              companyName: item.companyName || "",
              dba: item.dba || "",
              people: carrierPeople,
              email: item.email || "",
              message: item.phoneNumber || "",
              mcNumber: item.mcNumber || "",
              dotNumber: item.dotNumber || "",
              carrierAddress: {
                address: item.address?.address || "",
                city: item.address?.city || "",
                state: item.address?.state || "",
                zipCode: item.address?.zipCode || "",
              },
              equipment: equipmentGroups,
              capabilities: item.capabilities || [],
              certifications: item.certifications || [],
              carrierRouting: item.routing || "",
              carrierBankAccount: item.bankAccount || "",
              carrierAccountNumber: item.accountNumber || "",
              existingCarrierFiles: item.pdfs || [],
              carrierFiles: [],
            });
          }
        } catch (error) {
          showResponseModal(
            error?.response?.data?.message ||
              error?.message ||
              "Failed to load data",
            "error",
            "Error Loading Data"
          );
        } finally {
          setLoading(false);
        }
      };
      fetchItemData();
    }
  }, [
    isEditMode,
    itemId,
    isOpen,
    normalizedFormType,
    userData,
    reset,
    isUserForm,
    isCustomerForm,
    isCarrierForm,
  ]);

  // Load available customers/users for selection when modal opens
  // For User form: load ALL customers via getCustomers (getAll) AND selected via getAllowedCustomers (allowed)
  // For Customer form: load ALL users via getUsers (getAll) AND selected via getAllowedUsers (allowed)
  useEffect(() => {
    if (!isOpen) {
      selectionDataLoadedRef.current = false; // Reset when modal closes
      return;
    }

    // Prevent duplicate loading
    if (selectionDataLoadedRef.current) return;

    const loadSelectionData = async () => {
      if (isUserForm && isEditMode && isOpen) {
        try {
          setLoadingCustomers(true);
          selectionDataLoadedRef.current = true; // Mark as loading
          
          // 1. Load ALL customers (getAll) - full list for selection
          const allCustomersResponse = await customerService.getCustomers();
          const allCustomers = allCustomersResponse?.data?.data || allCustomersResponse?.data || [];
          setAvailableCustomers(Array.isArray(allCustomers) ? allCustomers : []);

          // 2. Загружаем уже привязанных customers для ЭТОГО конкретного user через эндпоинт
          if (itemId) {
            try {
              const allowedCustomersResponse = await userService.getAllowedCustomers(itemId);
              const allowedCustomers = allowedCustomersResponse?.data?.data || [];
              
              if (Array.isArray(allowedCustomers) && allowedCustomers.length > 0) {
                setSelectedCustomersData(allowedCustomers);
                const allowedIds = allowedCustomers.map((c) => (c.id || c._id).toString());
                if (JSON.stringify(formValues.allowedCustomers || []) !== JSON.stringify(allowedIds)) {
                  setValue('allowedCustomers', allowedIds, { shouldDirty: false });
                }
              } else {
                setSelectedCustomersData([]);
                setValue('allowedCustomers', [], { shouldDirty: false });
              }
            } catch (error) {
              console.error("Failed to load allowed customers:", error);
              setSelectedCustomersData([]);
            }
          } else {
            setSelectedCustomersData([]);
            setValue('allowedCustomers', [], { shouldDirty: false });
          }
        } catch (error) {
          console.error("Failed to load customers:", error);
          setAvailableCustomers([]);
          selectionDataLoadedRef.current = false; // Reset on error
        } finally {
          setLoadingCustomers(false);
        }
      } else if (isCustomerForm && isEditMode && isOpen) {
        try {
          setLoadingUsers(true);
          selectionDataLoadedRef.current = true; // Mark as loading
          
          // 1. Load ALL users (getAll) - full list for selection
          const allUsersResponse = await userService.getUsers();
          const allUsers = allUsersResponse?.data?.data || allUsersResponse?.data || [];
          setAvailableUsers(Array.isArray(allUsers) ? allUsers : []);

          // 2. Загружаем уже привязанных users для ЭТОГО конкретного customer через эндпоинт
          if (itemId) {
            try {
              const allowedUsersResponse = await customerService.getAllowedUsers(itemId);
              const allowedUsers = allowedUsersResponse?.data?.data || [];
              
              if (Array.isArray(allowedUsers) && allowedUsers.length > 0) {
                setSelectedUsersData(allowedUsers);
                const allowedIds = allowedUsers.map((u) => (u.id || u._id).toString());
                if (JSON.stringify(formValues.allowedUsers || []) !== JSON.stringify(allowedIds)) {
                  setValue('allowedUsers', allowedIds, { shouldDirty: false });
                }
              } else {
                setSelectedUsersData([]);
                setValue('allowedUsers', [], { shouldDirty: false });
              }
            } catch (error) {
              console.error("Failed to load allowed users:", error);
              setSelectedUsersData([]);
            }
          } else {
            setSelectedUsersData([]);
            setValue('allowedUsers', [], { shouldDirty: false });
          }
        } catch (error) {
          console.error("Failed to load users:", error);
          setAvailableUsers([]);
          selectionDataLoadedRef.current = false; // Reset on error
        } finally {
          setLoadingUsers(false);
        }
      }
    };

    if (isOpen && isEditMode && !selectionDataLoadedRef.current && itemId) {
      loadSelectionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, isUserForm, isCustomerForm, itemId]);

  // Watch only specific fields to avoid unnecessary re-renders
  const allowedCustomersIds = watch("allowedCustomers");
  const allowedUsersIds = watch("allowedUsers");

  // Update selected data when form values change (user selects/deselects items)
  useEffect(() => {
    if (isUserForm && availableCustomers.length > 0 && allowedCustomersIds) {
      const selectedIds = (allowedCustomersIds || []).map((id) => id.toString());
      const selected = availableCustomers.filter((c) =>
        selectedIds.includes((c.id || c._id).toString())
      );
      // Remove duplicates by ID
      const uniqueSelected = selected.filter((c, index, self) =>
        index === self.findIndex((item) => (item.id || item._id).toString() === (c.id || c._id).toString())
      );
      // Use functional update to always get latest state
      setSelectedCustomersData((prev) => {
        const prevIds = prev.map((c) => (c.id || c._id).toString()).sort();
        const newIds = uniqueSelected.map((c) => (c.id || c._id).toString()).sort();
        if (JSON.stringify(prevIds) !== JSON.stringify(newIds)) {
          return uniqueSelected;
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserForm, availableCustomers, allowedCustomersIds]);

  useEffect(() => {
    if (isCustomerForm && availableUsers.length > 0 && allowedUsersIds) {
      const selectedIds = (allowedUsersIds || []).map((id) => id.toString());
      const selected = availableUsers.filter((u) =>
        selectedIds.includes((u.id || u._id).toString())
      );
      // Remove duplicates by ID
      const uniqueSelected = selected.filter((u, index, self) =>
        index === self.findIndex((item) => (item.id || item._id).toString() === (u.id || u._id).toString())
      );
      // Use functional update to always get latest state
      setSelectedUsersData((prev) => {
        const prevIds = prev.map((u) => (u.id || u._id).toString()).sort();
        const newIds = uniqueSelected.map((u) => (u.id || u._id).toString()).sort();
        if (JSON.stringify(prevIds) !== JSON.stringify(newIds)) {
          return uniqueSelected;
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomerForm, availableUsers, allowedUsersIds]);

  // File handlers
  const handleUserFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setUserFileError("");
    const MAX_SIZE = 5 * 1024 * 1024;
    const validFiles = [];
    
    for (const file of files) {
      if (file.type !== "application/pdf") {
        setUserFileError("Only PDF files are allowed");
        return;
      }
      if (file.size > MAX_SIZE) {
        setUserFileError(
          `File "${file.name}" is too large. Maximum size is 5MB per file`
        );
        return;
      }
      validFiles.push(file);
    }
    
    setValue("userFiles", [...formValues.userFiles, ...validFiles]);
    e.target.value = "";
  };

  const handleRemoveUserFile = (index, isExisting = false) => {
    if (isExisting) {
      const newFiles = formValues.existingUserFiles.filter(
        (_, i) => i !== index
      );
      setValue("existingUserFiles", newFiles);
    } else {
      const newFiles = formValues.userFiles.filter((_, i) => i !== index);
      setValue("userFiles", newFiles);
    }
  };

  const handleCustomerFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setCustomerFileError("");
    const MAX_SIZE = 5 * 1024 * 1024;
    const validFiles = [];
    
    for (const file of files) {
      if (file.type !== "application/pdf") {
        setCustomerFileError("Only PDF files are allowed");
        return;
      }
      if (file.size > MAX_SIZE) {
        setCustomerFileError(
          `File "${file.name}" is too large. Maximum size is 5MB per file`
        );
        return;
      }
      validFiles.push(file);
    }
    
    setValue("customerFiles", [...formValues.customerFiles, ...validFiles]);
    e.target.value = "";
  };

  const handleRemoveCustomerFile = (index, isExisting = false) => {
    if (isExisting) {
      const newFiles = formValues.existingCustomerFiles.filter(
        (_, i) => i !== index
      );
      setValue("existingCustomerFiles", newFiles);
    } else {
      const newFiles = formValues.customerFiles.filter((_, i) => i !== index);
      setValue("customerFiles", newFiles);
    }
  };

  const handleCarrierFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setCarrierFileError("");
    const MAX_SIZE = 5 * 1024 * 1024;
    const validFiles = [];
    
    for (const file of files) {
      if (file.type !== "application/pdf") {
        setCarrierFileError("Only PDF files are allowed");
        return;
      }
      if (file.size > MAX_SIZE) {
        setCarrierFileError(
          `File "${file.name}" is too large. Maximum size is 5MB per file`
        );
        return;
      }
      validFiles.push(file);
    }
    
    setValue("carrierFiles", [...formValues.carrierFiles, ...validFiles]);
    e.target.value = "";
  };

  const handleRemoveCarrierFile = (index, isExisting = false) => {
    if (isExisting) {
      const newFiles = formValues.existingCarrierFiles.filter(
        (_, i) => i !== index
      );
      setValue("existingCarrierFiles", newFiles);
    } else {
      const newFiles = formValues.carrierFiles.filter((_, i) => i !== index);
      setValue("carrierFiles", newFiles);
    }
  };

  // Avatar handlers
  const onPickAvatar = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarUrl(reader.result);
        setValue("avatarFile", file);
      };
      reader.readAsDataURL(file);
      setShowAvatarMenu(false);
    }
  };

  const handleDeleteAvatar = () => {
    setAvatarUrl(null);
    setValue("avatarFile", null);
    setShowAvatarMenu(false);
  };

  const openAvatarMenu = () => {
    setShowAvatarMenu(!showAvatarMenu);
  };

  // Equipment type handlers
  const handleAddEquipmentGroup = () => {
    const equipment = Array.isArray(formValues.equipment) ? formValues.equipment : [];
    setValue("equipment", [...equipment, createEmptyEquipmentGroup()]);
  };

  const handleRemoveEquipmentGroup = (groupIndex) => {
    const equipment = Array.isArray(formValues.equipment) ? formValues.equipment : [];
    if (equipment.length > 1) {
      setValue("equipment", equipment.filter((_, index) => index !== groupIndex));
    }
  };

  const handleEquipmentTypeChange = (groupIndex, value) => {
    const equipment = Array.isArray(formValues.equipment) ? [...formValues.equipment] : [];
    equipment[groupIndex] = {
      ...equipment[groupIndex],
      type: value
    };
    setValue("equipment", equipment);
  };

  // Size handlers
  const handleAddSize = (groupIndex) => {
    const equipment = Array.isArray(formValues.equipment) ? [...formValues.equipment] : [];
    const group = equipment[groupIndex] || createEmptyEquipmentGroup();
    const sizes = [...(group.sizes || []), { value: "", custom: "" }];
    equipment[groupIndex] = { ...group, sizes };
    setValue("equipment", equipment);
  };

  const handleRemoveSize = (groupIndex, sizeIndex) => {
    const equipment = Array.isArray(formValues.equipment) ? [...formValues.equipment] : [];
    const group = equipment[groupIndex] || createEmptyEquipmentGroup();
    const sizes = (group.sizes || []).filter((_, index) => index !== sizeIndex);
    equipment[groupIndex] = {
      ...group,
      sizes: sizes.length > 0 ? sizes : [{ value: "", custom: "" }]
    };
    setValue("equipment", equipment);
  };

  const handleSizeChange = (groupIndex, sizeIndex, value) => {
    const equipment = Array.isArray(formValues.equipment) ? [...formValues.equipment] : [];
    const group = equipment[groupIndex] || createEmptyEquipmentGroup();
    const sizes = [...(group.sizes || [])];
    sizes[sizeIndex] = {
      value,
      custom: value === "Other" ? sizes[sizeIndex]?.custom || "" : ""
    };
    equipment[groupIndex] = { ...group, sizes };
    setValue("equipment", equipment);
  };

  const handleCustomSizeChange = (groupIndex, sizeIndex, value) => {
    const equipment = Array.isArray(formValues.equipment) ? [...formValues.equipment] : [];
    const group = equipment[groupIndex] || createEmptyEquipmentGroup();
    const sizes = [...(group.sizes || [])];
    sizes[sizeIndex] = { ...sizes[sizeIndex], custom: value };
    equipment[groupIndex] = { ...group, sizes };
    setValue("equipment", equipment);
  };

  // Capabilities/Certifications handlers
  const handleCheckboxChange = (field, value) => {
    const current = formValues[field] || [];
    const newValue = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    setValue(field, newValue);
  };

  // Form submission
  const onSubmit = async (data) => {
    // Validate equipment types for carrier form
    if (isCarrierForm) {
      const equipmentPayload = buildEquipmentPayload(data.equipment || []);
      if (equipmentPayload.length === 0) {
        showResponseModal(
          "At least one Equipment Type is required",
          "error",
          "Validation Error"
        );
        return;
      }
    }

    setLoading(true);

    try {
      if (isUserForm) {
        const nameParts = data.fullName.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        const userDataPayload = {
          firstName,
          lastName,
          email: data.email.trim().toLowerCase(),
          companyName: data.companyName.trim(),
          ...(data.phone != null && data.phone !== "" && { phoneNumber: data.phone.trim() }),
          role: data.role,
          status: data.active ? "active" : "suspended",
          ...(data.password && { password: data.password }),
          ...(isEditMode && {
            allowedCustomers: Array.isArray(data.allowedCustomers) ? data.allowedCustomers : [],
          }),
        };

        const hasFiles = data.avatarFile || data.userFiles.length > 0;
        let formData;

        if (hasFiles) {
          formData = new FormData();
          // Append all fields except allowedCustomers (we'll add it separately as array)
          Object.keys(userDataPayload).forEach((key) => {
            if (key !== "allowedCustomers") {
              const value = userDataPayload[key];
              if (value !== undefined && value !== null) {
                formData.append(key, value);
              }
            }
          });
          // Always send allowedCustomers in edit mode (as array of IDs, even if empty)
          // ВАЖНО: всегда отправляем массив, даже если он пустой - это означает удаление всех связей
          if (isEditMode) {
            const allowedCustomersArray = Array.isArray(data.allowedCustomers) ? data.allowedCustomers : [];
            if (allowedCustomersArray.length > 0) {
              allowedCustomersArray.forEach((customerId) => {
                formData.append("allowedCustomers[]", customerId);
              });
            } else {
              // Отправляем пустой массив - добавляем пустое значение
              formData.append("allowedCustomers[]", "");
            }
          }
          if (data.avatarFile) {
            formData.append("profileImage", data.avatarFile);
          }
          data.userFiles.forEach((file) => {
            formData.append("pdfs", file);
          });
          formData.append(
            "existingPdfs",
            JSON.stringify(data.existingUserFiles)
          );
        } else {
          formData = {
            ...userDataPayload,
            ...(isEditMode && { existingPdfs: data.existingUserFiles }),
            // Always include allowedCustomers in edit mode (even if empty array)
            ...(isEditMode &&
              Array.isArray(data.allowedCustomers) && {
                allowedCustomers: data.allowedCustomers,
              }),
          };
        }

        if (isEditMode && itemId) {
          await userService.updateUser(itemId, formData);
        } else {
          await userService.createUser(formData);
        }
      } else if (isCustomerForm) {
        // Get current representativePeoples from formValues (watch) to ensure we have latest data
        const currentRepresentativePeoples = Array.isArray(formValues.representativePeoples)
          ? formValues.representativePeoples
          : (Array.isArray(data.representativePeoples) ? data.representativePeoples : []);
        
        // Filter representativePeoples before creating payload
        const filteredRepresentativePeoples = filterEmptyArrayItems(currentRepresentativePeoples, {
          customFilter: (person) => person && person.fullName && person.fullName.trim()
        });
        
        const customerDataPayload = {
          companyName: data.companyName.trim(),
          type: data.type || "customer",
          ...(data.type !== "platform" && {
            customerAddress: {
              address: data.customerAddress?.address?.trim() || "",
              city: data.customerAddress?.city?.trim() || "",
              state: data.customerAddress?.state?.trim() || "",
              zipCode: data.customerAddress?.zipCode?.trim() || "",
            },
          }),
          ...(data.email && { email: data.email.trim().toLowerCase() }),
          ...(data.message && { phoneNumber: data.message.trim() }),
          ...(data.customerPaymentTerms && {
            paymentTerms: data.customerPaymentTerms.trim(),
          }),
          ...(data.customerCreditLimit && {
            creditLimit: parseFloat(data.customerCreditLimit) || 0,
          }),
          // Include representativePeoples only if not empty
          ...(filteredRepresentativePeoples && {
            representativePeoples: filteredRepresentativePeoples
          }),
          // Always include allowedUsers in edit mode if it's an array
          ...(isEditMode &&
            Array.isArray(data.allowedUsers) && {
              allowedUsers: data.allowedUsers,
            }),
        };

        const hasFiles = data.customerFiles.length > 0;
        let formData;

        if (hasFiles) {
          formData = new FormData();
          formData.append("companyName", customerDataPayload.companyName);
          formData.append("type", customerDataPayload.type);
          if (customerDataPayload.customerAddress) {
            formData.append(
              "customerAddress[address]",
              customerDataPayload.customerAddress.address
            );
            formData.append(
              "customerAddress[city]",
              customerDataPayload.customerAddress.city
            );
            formData.append(
              "customerAddress[state]",
              customerDataPayload.customerAddress.state
            );
            formData.append(
              "customerAddress[zipCode]",
              customerDataPayload.customerAddress.zipCode
            );
          }
          if (data.email) {
            formData.append("email", data.email.trim().toLowerCase());
          }
          if (data.message) {
            formData.append("phoneNumber", data.message.trim());
          }
          if (data.customerPaymentTerms) {
            formData.append("paymentTerms", data.customerPaymentTerms.trim());
          }
          if (data.customerCreditLimit) {
            formData.append(
              "creditLimit",
              parseFloat(data.customerCreditLimit) || 0
            );
          }
          // Filter and send representativePeoples only if not empty
          // Get current representativePeoples from formValues (watch) to ensure we have latest data
          const currentRepresentativePeoples = Array.isArray(formValues.representativePeoples)
            ? formValues.representativePeoples
            : (Array.isArray(data.representativePeoples) ? data.representativePeoples : []);
          const representativePeoplesArray = filterEmptyArrayItems(currentRepresentativePeoples, {
            customFilter: (person) => person && person.fullName && person.fullName.trim()
          });
          if (representativePeoplesArray) {
            formData.append(
              "representativePeoples",
              JSON.stringify(representativePeoplesArray)
            );
          }
          // Always send allowedUsers in edit mode (as array of IDs, even if empty)
          // ВАЖНО: всегда отправляем массив, даже если он пустой - это означает удаление всех связей
          if (isEditMode) {
            const allowedUsersArray = Array.isArray(data.allowedUsers) ? data.allowedUsers : [];
            if (allowedUsersArray.length > 0) {
              allowedUsersArray.forEach((userId) => {
                formData.append("allowedUsers[]", userId);
              });
            } else {
              // Отправляем пустой массив - добавляем пустое значение
              formData.append("allowedUsers[]", "");
            }
          }
          data.customerFiles.forEach((file) => {
            formData.append("pdfs", file);
          });
          formData.append(
            "existingPdfs",
            JSON.stringify(data.existingCustomerFiles)
          );
        } else {
          formData = {
            ...customerDataPayload,
            ...(isEditMode && { existingPdfs: data.existingCustomerFiles }),
          };
        }

        if (isEditMode && itemId) {
          await customerService.updateCustomer(itemId, formData);
        } else {
          await customerService.createCustomer(formData);
        }
      } else if (isCarrierForm) {
        const equipmentPayload = buildEquipmentPayload(data.equipment || []);
        const equipmentMeta = deriveEquipmentMeta(equipmentPayload);
        const carrierDataPayload = {
          name: data.fullName.trim() || data.companyName.trim(),
          companyName: data.companyName.trim(),
          ...(data.dba != null && data.dba !== "" && { dba: data.dba.trim() }),
          people: Array.isArray(data.people) ? data.people : [],
          mcNumber: data.mcNumber.trim(),
          dotNumber: data.dotNumber.trim(),
          ...(data.email && data.email.trim() !== '' && { email: data.email.trim().toLowerCase() }),
          ...(data.message && data.message.trim() !== '' && { phoneNumber: data.message.trim() }),
          address: {
            address: data.carrierAddress.address.trim() || "",
            city: data.carrierAddress.city.trim() || "",
            state: data.carrierAddress.state.trim() || "",
            zipCode: data.carrierAddress.zipCode.trim() || "",
          },
          equipment: equipmentPayload,
          equipmentType: equipmentMeta.equipmentType,
          size: equipmentMeta.size,
          ...(data.capabilities.length > 0 && {
            capabilities: data.capabilities,
          }),
          ...(data.certifications.length > 0 && {
            certifications: data.certifications,
          }),
          ...(data.carrierRouting && { routing: data.carrierRouting.trim() }),
          ...(data.carrierBankAccount && {
            bankAccount: data.carrierBankAccount.trim(),
          }),
          ...(data.carrierAccountNumber && {
            accountNumber: data.carrierAccountNumber.trim(),
          }),
        };

        const hasFiles = data.carrierFiles.length > 0;
        let formData;

        if (hasFiles) {
          formData = new FormData();
          Object.keys(carrierDataPayload).forEach((key) => {
            if (key === "address") {
              Object.keys(carrierDataPayload.address).forEach((addrKey) => {
                formData.append(
                  `address[${addrKey}]`,
                  carrierDataPayload.address[addrKey]
                );
              });
            } else if (key === "equipment") {
              formData.append(key, JSON.stringify(carrierDataPayload[key]));
            } else if (key === "equipmentType" || key === "size") {
              (carrierDataPayload[key] || []).forEach((item) => {
                formData.append(`${key}[]`, item);
              });
            } else if (key === "capabilities" || key === "certifications" || key === "people") {
              formData.append(key, JSON.stringify(carrierDataPayload[key]));
            } else {
              // Ensure email and phoneNumber are always sent, even if empty
              const value = carrierDataPayload[key];
              if (value !== undefined && value !== null) {
                formData.append(key, typeof value === 'string' ? value : JSON.stringify(value));
              }
            }
          });
          data.carrierFiles.forEach((file) => {
            formData.append("pdfs", file);
          });
          formData.append(
            "existingPdfs",
            JSON.stringify(data.existingCarrierFiles)
          );
        } else {
          formData = {
            ...carrierDataPayload,
            ...(isEditMode && { existingPdfs: data.existingCarrierFiles }),
          };
        }

        if (isEditMode && itemId) {
          await carrierService.updateCarrier(itemId, formData);
        } else {
          await carrierService.createCarrier(formData);
        }
      }

      showResponseModal(
        `${
          normalizedFormType === "users"
            ? "User"
            : normalizedFormType === "customers"
            ? "Customer"
            : "Carrier"
        } ${isEditMode ? "updated" : "created"} successfully`,
        "success",
        "Success"
      );

      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
      handleClose();
        }, 1000);
      } else {
        setTimeout(() => handleClose(), 1000);
      }
    } catch (error) {
      showResponseModal(
        error?.response?.data?.message ||
          error?.message ||
          `Failed to ${isEditMode ? "update" : "create"} ${normalizedFormType}`,
        "error",
        "Error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setAvatarUrl(null);
    setShowPassword(false);
    setShowConfirm(false);
    setShowAvatarMenu(false);
    if (typeof setIsOpen === "function") {
      setIsOpen(false);
    }
    if (typeof onClose === "function") onClose();
  };

  if (!isOpen) return null;

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      handleClose();
    }
  };

  const getFileName = (url) => {
    if (!url) return "";
    try {
      const urlPath = new URL(url).pathname;
      return decodeURIComponent(urlPath.split("/").pop() || "document.pdf");
    } catch {
      return url.split("/").pop() || "document.pdf";
    }
  };

  // Continue with JSX rendering...
  // Due to file size, I'll create a simplified version showing the structure
  // The full JSX would include all form fields using register() and Controller

  return (
    <>
      {createPortal(
        <div className="modal-overlay" >
      <div
        ref={dialogRef}
        className="modal-content-wrapper"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
      >
            <form
              onSubmit={handleSubmit(onSubmit)}
              className={`add-user-modal add-user-modal--${normalizedFormType}`}
            >
          <div className="modal-header">
            <h2 className="modal-title">
              {isEditMode
                    ? isUserForm
                      ? "Edit User"
                      : isCustomerForm
                      ? "Edit Customer"
                      : "Edit Carrier"
                    : isUserForm
                    ? "Add New User"
                    : isCustomerForm
                    ? "Add New Customer"
                    : "Add New Carrier"}
            </h2>
                <button
                  type="button"
                  className="close-button"
                  onClick={handleClose}
                  aria-label="Close modal"
                >
              <X className="close-icon" />
            </button>
          </div>

          <div className="modal-body">
                {/* Avatar Section for Users */}
            {isUserForm && (
                  <AvatarSection
                    avatarUrl={avatarUrl}
                    showAvatarMenu={showAvatarMenu}
                    onOpenMenu={openAvatarMenu}
                    onPickAvatar={onPickAvatar}
                    onDeleteAvatar={handleDeleteAvatar}
                  />
                )}

                {/* User Form Fields */}
            {isUserForm && (
                  <UserFormFields
                    register={register}
                    control={control}
                    errors={errors}
                    formValues={formValues}
                    watch={watch}
                    setValue={setValue}
                    isEditMode={isEditMode}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    showConfirm={showConfirm}
                    setShowConfirm={setShowConfirm}
                    handleUserFilesChange={handleUserFilesChange}
                    handleRemoveUserFile={handleRemoveUserFile}
                    getFileName={getFileName}
                    userFileError={userFileError}
                    selectedCustomersData={selectedCustomersData}
                    availableCustomers={availableCustomers}
                    loadingCustomers={loadingCustomers}
                    showCustomersDropdown={showCustomersDropdown}
                    setShowCustomersDropdown={setShowCustomersDropdown}
                    customerSearchTerm={customerSearchTerm}
                    setCustomerSearchTerm={setCustomerSearchTerm}
                  />
                )}

                {/* Customer Form Fields */}
                {isCustomerForm && (
                  <CustomerFormFields
                    register={register}
                    errors={errors}
                    formValues={formValues}
                    setValue={setValue}
                    watch={watch}
                    isEditMode={isEditMode}
                    handleCustomerFilesChange={handleCustomerFilesChange}
                    handleRemoveCustomerFile={handleRemoveCustomerFile}
                    getFileName={getFileName}
                    customerFileError={customerFileError}
                    selectedUsersData={selectedUsersData}
                    availableUsers={availableUsers}
                    loadingUsers={loadingUsers}
                    userSearchTerm={userSearchTerm}
                    setUserSearchTerm={setUserSearchTerm}
                    showUsersDropdown={showUsersDropdown}
                    setShowUsersDropdown={setShowUsersDropdown}
                  />
                )}

                {/* Carrier Form Fields */}
                {isCarrierForm && (
                  <CarrierFormFields
                    register={register}
                    errors={errors}
                    formValues={formValues}
                    setValue={setValue}
                    watch={watch}
                    control={control}
                    handleAddEquipmentGroup={handleAddEquipmentGroup}
                    handleRemoveEquipmentGroup={handleRemoveEquipmentGroup}
                    handleEquipmentTypeChange={handleEquipmentTypeChange}
                    handleAddSize={handleAddSize}
                    handleRemoveSize={handleRemoveSize}
                    handleSizeChange={handleSizeChange}
                    handleCustomSizeChange={handleCustomSizeChange}
                    handleCheckboxChange={handleCheckboxChange}
                    handleCarrierFilesChange={handleCarrierFilesChange}
                    handleRemoveCarrierFile={handleRemoveCarrierFile}
                    getFileName={getFileName}
                    carrierFileError={carrierFileError}
                    equipmentTypeOptions={equipmentTypeOptions}
                    sizeOptions={sizeOptions}
                    capabilitiesOptions={capabilitiesOptions}
                    certificationsOptions={certificationsOptions}
                  />
                )}

            <div className="action-buttons">
              <button 
                className="cancel-button" 
                type="button" 
                    onClick={handleClose}
                    disabled={loading || isSubmitting}
              >
                Cancel
              </button>
              <button 
                className="add-user-button" 
                    type="submit"
                    disabled={loading || isSubmitting}
                  >
                    {loading || isSubmitting
                      ? isEditMode
                        ? "Updating..."
                        : "Adding..."
                  : isEditMode
                      ? isUserForm
                        ? "Update User"
                        : isCustomerForm
                        ? "Update Customer"
                        : "Update Carrier"
                      : isUserForm
                      ? "Add User"
                      : isCustomerForm
                      ? "Add Customer"
                      : "Add Carrier"}
              </button>
            </div>
          </div>
            </form>
        </div>
        </div>,
        document.body
      )}
      {createPortal(
        <ResponseModal
          isOpen={responseModal.isOpen}
          message={responseModal.message}
          status={responseModal.status}
          title={responseModal.title}
          onClose={closeResponseModal}
        />,
        document.body
      )}
    </>
  );
}

export default AddForm;
